import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface ROIAnalysisChartProps {
  data: Array<{
    name: string;
    roi: number;
    total_investment: number;
    total_return: number;
    workshop_count: number;
  }>;
  loading?: boolean;
}

const ROIAnalysisChart: React.FC<ROIAnalysisChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ROI Analysis</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ROI Analysis</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No ROI data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const getROIColor = (roi: number) => {
    if (roi >= 300) return '#10b981'; // Green for excellent ROI
    if (roi >= 200) return '#3b82f6'; // Blue for good ROI
    if (roi >= 100) return '#f59e0b'; // Yellow for average ROI
    return '#ef4444'; // Red for poor ROI
  };

  const getROILevel = (roi: number) => {
    if (roi >= 300) return 'Excellent';
    if (roi >= 200) return 'Good';
    if (roi >= 100) return 'Average';
    return 'Poor';
  };

  const maxROI = Math.max(...data.map(d => d.roi));
  const avgROI = data.reduce((sum, item) => sum + item.roi, 0) / data.length;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">ROI Analysis</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 20']}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => {
                if (name === 'roi') return [`${value.toFixed(1)}%`, 'ROI'];
                if (name === 'total_investment') return [formatCurrency(value), 'Total Investment'];
                if (name === 'total_return') return [formatCurrency(value), 'Total Return'];
                return [value, name];
              }}
              labelFormatter={(label) => `Entity: ${label}`}
            />
            <Bar dataKey="roi" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getROIColor(entry.roi)} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">ROI Performance</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.roi - a.roi)
              .map((item) => {
                const level = getROILevel(item.roi);
                const color = getROIColor(item.roi);
                return (
                  <div key={item.name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: color }}
                      ></div>
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.roi.toFixed(1)}%</span>
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: color + '20', 
                        color: color 
                      }}>
                        {level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">ROI Summary</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Best ROI</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.roi > max.roi ? item : max, data[0])?.name}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {maxROI.toFixed(1)}% return
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Average ROI</span>
                <span className="font-bold text-green-600">{avgROI.toFixed(1)}%</span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                Across all entities
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Investment</span>
                <span className="font-bold text-gray-900">
                  {formatCurrency(data.reduce((sum, item) => sum + item.total_investment, 0))}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across {data.length} entities
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROIAnalysisChart;

